'use client';

import * as amplitude from '@amplitude/analytics-browser';
import { ExperimentClient } from '@amplitude/experiment-js-client';
import SHA1 from 'crypto-js/sha1';
import { useSearchParams } from 'next/navigation';
import { createContext, useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react';

import { initDatadogLogs } from '@calm-web/logger';

import { LogEventArgs, LogPurchaseArgs } from '@/hooks/analytics/types';
import { useShareToken } from '@/hooks/share/useShareToken';
import {
	useCampaignState,
	useComplianceLevelState,
	useDeviceState,
	usePurchaseParamsState,
	useUserState,
} from '@/hooks/store';
import { NO_RESTRICTIONS, REQUIRE_GDPR_MODAL } from '@/store/complianceLevel/types';
import {
	initAmplitude,
	logAmplitudeEvent,
	logAmplitudePurchase,
	setAmplitudeDeviceProperties,
	setAmplitudeUserId,
	setAmplitudeUserProperties,
} from '@/utils/analytics/amplitude';
import { initDatadogRum } from '@/utils/analytics/datadogRum';
import { logGtmEvent } from '@/utils/analytics/gtm';
import setupClassicEventProps, { setupSleepEventProps } from '@/utils/analytics/setupEventProps';
import { getUserData } from '@/utils/apiRequest/getUserData';
import { parsePartnerDetails } from '@/utils/b2b/partners';
import { calmLogger } from '@/utils/calmLogger';
import { getCookie } from '@/utils/cookies';
import { postDevice } from '@/utils/endpoints';
import {
	AMPLITUDE_EXPERIMENTS_UNINITIALIZED_STATE,
	AmplitudeExperimentClient,
	initAmplitudeExperimentClient,
	setupAmplitudeExperimentUser,
} from '@/utils/experiments/amplitudeExperiment';
import { getUserIdForAnalytics } from '@/utils/getUserIdForAnalytics';
import { CALM_DEVICE_ID } from '@/utils/privacyCookies';

export const forciblyLoggedEventNames = [
	'Cookie Preferences : Essential Only',
	'Cookie Preferences : Accept All',
];

export type LogEventResponse = amplitude.Types.Result | string | void | null;

export type LogEventAsync = ({ eventName, eventProps }: LogEventArgs) => Promise<LogEventResponse>;
export type LogEvent = ({ eventName, eventProps }: LogEventArgs) => LogEventResponse;

export interface AnalyticsContextProps {
	logEvent: LogEvent;
	logEventAsync: LogEventAsync;
	logPurchase: ({ productInfo, transactionInfo }: LogPurchaseArgs) => LogEventResponse;
	experimentClient?: AmplitudeExperimentClient;
	isAnalyticsInitialized: boolean;
}

export const AnalyticsContext = createContext<AnalyticsContextProps>({
	logEvent: () => {},
	logEventAsync: async () => {},
	logPurchase: () => {},
	isAnalyticsInitialized: false,
});

function useTrackPartnerDetails(): void {
	const user = useUserState();
	const complianceLevel = useComplianceLevelState();

	const canLogEvent = complianceLevel !== REQUIRE_GDPR_MODAL;
	const { partnerId, partnerName } = useMemo(() => parsePartnerDetails(user), [user]);

	useEffect(() => {
		if (canLogEvent && (partnerId || partnerName)) {
			setAmplitudeUserProperties({
				...(partnerId && { partner_id: partnerId }),
				...(partnerName && { partner_name: partnerName }),
			});
		}
	}, [canLogEvent, partnerId, partnerName]);
}

export type AnalyticsContextProviderProps = PropsWithChildren<{
	amplitudeExperimentsDeploymentKey?: string;
	amplitudeToken?: string;
}>;

const AnalyticsContextProvider = ({
	children,
	amplitudeExperimentsDeploymentKey,
	amplitudeToken,
}: AnalyticsContextProviderProps) => {
	const searchParams = useSearchParams();
	const device = useDeviceState();
	const user = useUserState();
	const complianceLevel = useComplianceLevelState();
	const purchaseParams = usePurchaseParamsState();
	const campaign = useCampaignState();
	const shareToken = useShareToken();

	const canLogEvent = complianceLevel !== REQUIRE_GDPR_MODAL;

	const calmUserId = getUserIdForAnalytics(user, searchParams);
	const deviceId = device?.deviceId ?? getCookie(CALM_DEVICE_ID);
	const userEmail = user?.email;

	const [experimentClient, setExperimentClient] = useState<AmplitudeExperimentClient>();
	const [isAnalyticsInitialized, setIsAnalyticsInitialized] = useState<boolean>(false);

	const initExperiments = useCallback(async () => {
		const amplitudeExperimentClient = await initAmplitudeExperimentClient(amplitudeExperimentsDeploymentKey);
		setExperimentClient(amplitudeExperimentClient);
	}, [amplitudeExperimentsDeploymentKey]);

	useEffect(() => {
		if (isAnalyticsInitialized) return;
		if (!canLogEvent) {
			// TODO: refactor this code to follow best practices
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setExperimentClient(AMPLITUDE_EXPERIMENTS_UNINITIALIZED_STATE);
			return;
		}

		const initLibs = async () => {
			await initAmplitude(amplitudeToken, deviceId);
			await initExperiments();
			initDatadogRum();
			initDatadogLogs({
				service: 'web-www',
				version: process.env.BUILD_ID,
				env: process.env.NEXT_PUBLIC_LOG_ENV ?? 'local',
			});
			setIsAnalyticsInitialized(true);
		};

		const initialPostDevice = async () => {
			await postDevice({ userData: getUserData() });
		};

		initLibs().catch(error => {
			calmLogger.error('Error in AnalyticsContextProvider initLibs', {}, error);
			// Fail-safe: if analytics init fails before the experiment client is set,
			// settle it to the uninitialized sentinel so useAmplitudeExperiment resolves
			// to control (isLoading=false, not-enrolled) instead of hanging forever on a
			// never-arriving client — which left eligible refund users with a dead
			// "Request Refund" button. `prev ??` preserves a client already
			// set by initExperiments if a later init step (datadog) is what threw.
			setExperimentClient(prev => prev ?? AMPLITUDE_EXPERIMENTS_UNINITIALIZED_STATE);
		});
		initialPostDevice().catch(error =>
			calmLogger.error('Error in AnalyticsContextProvider initialPostDevice', {}, error),
		);
	}, [amplitudeToken, canLogEvent, initExperiments, isAnalyticsInitialized, deviceId]);

	useEffect(() => {
		async function fetchExperimentInfo(): Promise<ExperimentClient | null> {
			if (experimentClient && experimentClient !== AMPLITUDE_EXPERIMENTS_UNINITIALIZED_STATE) {
				const experimentUser = setupAmplitudeExperimentUser(user, device);
				const newClient = await experimentClient.fetch(experimentUser);
				return newClient;
			}
			return Promise.resolve(null);
		}
		fetchExperimentInfo()
			.then((refreshedExperimentClient: ExperimentClient | null) => {
				if (refreshedExperimentClient) {
					setExperimentClient(refreshedExperimentClient);
				}
			})
			.catch(error => {
				return calmLogger.error('Error in AnalyticsContextProvider fetchExperimentInfo', {}, error);
			});
	}, [device, experimentClient, user]);

	useEffect(() => {
		if (canLogEvent && calmUserId) {
			setAmplitudeUserId(calmUserId);
		}
	}, [canLogEvent, calmUserId]);

	useEffect(() => {
		if (canLogEvent && deviceId) {
			setAmplitudeDeviceProperties(deviceId);
		}
	}, [canLogEvent, deviceId]);

	useEffect(() => {
		const appsflyerId = getCookie('afUserId');
		if (canLogEvent && appsflyerId) {
			setAmplitudeUserProperties({ appsflyer_id: appsflyerId });
		}
	}, [canLogEvent]);

	useTrackPartnerDetails();

	// Defense in depth. logGtmEvent pushes whenever window.dataLayer exists, so this gates on the
	// tier as well.
	const canLogGtmEvent =
		process.env.NEXT_PUBLIC_CALM_ENV === 'production' && complianceLevel === NO_RESTRICTIONS;

	const logEventAsync = useCallback(
		async ({
			eventName,
			eventProps,
			logGtmEventOnly = false,
			hydrateSleepEventProps = false,
		}: LogEventArgs): Promise<LogEventResponse> => {
			if (!canLogEvent && !forciblyLoggedEventNames.includes(eventName)) return null;

			const isLoggedIn = Boolean(calmUserId);
			const hashedUserEmail = userEmail && SHA1(userEmail).toString();
			const shareEventProps =
				typeof shareToken === 'string' && shareToken.length > 0
					? {
							source: 'Shared Link',
							share_token: shareToken,
						}
					: {};

			const hydratedEventProps = hydrateSleepEventProps
				? await setupSleepEventProps(eventProps || {})
				: await setupClassicEventProps(
						eventProps || {},
						isLoggedIn,
						purchaseParams?.coupon,
						shareEventProps,
						complianceLevel,
						hashedUserEmail,
						campaign,
					);

			const logProps = {
				eventName,
				eventProps: hydratedEventProps,
			};

			if (canLogGtmEvent) {
				logGtmEvent(logProps);
			}

			if (process.env.NEXT_PUBLIC_CALM_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
				calmLogger.info(`Log event: ${eventName}`, eventProps);
			}

			if (!logGtmEventOnly) {
				return logAmplitudeEvent(logProps).catch(error => {
					calmLogger.error('Error in AnalyticsContextProvider logAmplitudeEvent', {}, error);
					return null;
				});
			}
		},
		[
			canLogEvent,
			calmUserId,
			userEmail,
			shareToken,
			purchaseParams?.coupon,
			complianceLevel,
			canLogGtmEvent,
			campaign,
		],
	);
	const logEvent = useCallback(
		(args: LogEventArgs): void => {
			logEventAsync(args)?.catch(error =>
				calmLogger.error('Error in AnalyticsContextProvider logEventAsync', {}, error),
			);
		},
		[logEventAsync],
	);

	const logPurchase = useCallback(
		({ productInfo, transactionInfo }: LogPurchaseArgs): void => {
			if (!canLogEvent) return;

			logAmplitudePurchase({ productInfo, transactionInfo }).catch(error =>
				calmLogger.error('Error in AnalyticsContextProvider logAmplitudePurchase', {}, error),
			);
		},
		[canLogEvent],
	);

	const value = useMemo(
		() => ({
			logEventAsync,
			logEvent,
			logPurchase,
			experimentClient,
			isAnalyticsInitialized,
		}),
		[logEventAsync, logEvent, logPurchase, experimentClient, isAnalyticsInitialized],
	);

	return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};

export default AnalyticsContextProvider;
